import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { QueryErrorCard } from "@/components/QueryErrorCard";
import { Guarded } from "@/components/Guarded";
import { AppPicker } from "@/components/fleet/AppPicker";
import { TaskAssistant } from "@/components/fleet/TaskAssistant";
import { SchemaEditor } from "@/components/fleet/SchemaEditor";
import { ScheduleBuilder } from "@/components/fleet/ScheduleBuilder";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Input, Textarea } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { Switch } from "@/components/ui/Switch";
import { useApiMutation, useApiQuery } from "@/hooks/useApi";
import { ApiError, get, post, put, qs } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { S, thinkingPolicyLabel, visionModeLabel } from "@/lib/strings";
import { variablesIn } from "@/lib/utils";
import type {
  DeviceSummary,
  Page,
  TaskRow,
  TaskSpec,
  ThinkingPolicy,
  VisionMode,
} from "@/types/fleet";
import { emptyTaskSpec } from "@/types/fleet";

const VISION_MODES: VisionMode[] = ["OFF", "AUTO", "ALWAYS"];
const THINKING_POLICIES: ThinkingPolicy[] = ["ADAPTIVE", "ALWAYS_ON", "ALWAYS_OFF"];

/**
 * Keep only the `TaskSpec` fields. The server's bookkeeping — version,
 * assignment_count, created_at — is not ours to echo back, and `TaskSpec` is
 * pushed to devices verbatim, so anything extra would end up on a phone.
 */
function toSpec(d: TaskRow): TaskSpec {
  return {
    id: d.id,
    name: d.name,
    goal: d.goal,
    allowedPackages: d.allowedPackages ?? [],
    blockedLabelsRegex: d.blockedLabelsRegex ?? null,
    allowSend: d.allowSend ?? false,
    extractionSchema: d.extractionSchema ?? null,
    schedule: d.schedule ?? emptyTaskSpec().schedule,
    visionMode: d.visionMode ?? "AUTO",
    thinkingPolicy: d.thinkingPolicy ?? "ADAPTIVE",
    maxSteps: d.maxSteps ?? 40,
    runTimeoutMinutes: d.runTimeoutMinutes ?? 15,
    webhookUrl: d.webhookUrl ?? null,
    requireUnlock: d.requireUnlock ?? true,
    enabled: d.enabled ?? true,
    createdAt: d.createdAt ?? 0,
    updatedAt: d.updatedAt ?? 0,
  };
}

export function TaskEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";

  const query = useApiQuery<TaskRow>(qk.task(id ?? ""), `/tasks/${id}`, {
    enabled: !isNew,
    refetchInterval: false,
  });

  if (isNew) return <TaskForm initial={emptyTaskSpec()} isNew />;

  if (query.isError) {
    return (
      <>
        <BackLink />
        <QueryErrorCard error={query.error} onRetry={() => void query.refetch()} />
      </>
    );
  }

  if (query.isPending || !query.data) {
    return (
      <>
        <BackLink />
        <div className="max-w-3xl space-y-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </>
    );
  }

  // Keyed on the task id so navigating between tasks remounts the form with
  // fresh state. Seeding a draft from an effect instead would mean one render
  // showing the previous task's values, and an editor that fights the operator
  // every time the underlying query refetches.
  return (
    <TaskForm
      key={query.data.id}
      initial={toSpec(query.data)}
      isNew={false}
      initialVariables={query.data.variable_defaults ?? {}}
    />
  );
}

/* ------------------------------------------------------------------ *
 * The form
 * ------------------------------------------------------------------ */

function TaskForm({
  initial,
  isNew,
  initialVariables = {},
}: {
  initial: TaskSpec;
  isNew: boolean;
  initialVariables?: Record<string, string>;
}) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<TaskSpec>(initial);
  const [variables, setVariables] = useState<Record<string, string>>(initialVariables);
  const [unusedVariables, setUnusedVariables] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Derived from the text, exactly as the server derives it, so the form cannot disagree
  // with what a dispatch will demand.
  const used = useMemo(() => variablesIn(draft), [draft]);

  // Fan-out needs a device count. Assignments would be the honest denominator,
  // but phase 1 has no assignment editor, so the fleet size stands in and the
  // warning says so.
  const devices = useQuery<Page<DeviceSummary>, ApiError>({
    queryKey: ["devices", "count-probe"],
    queryFn: () => get<Page<DeviceSummary>>(`/devices${qs({ limit: 200 })}`),
    staleTime: 5 * 60_000,
  });
  const deviceCount = devices.data?.items.length ?? null;

  const set = <K extends keyof TaskSpec>(key: K, value: TaskSpec[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const save = useApiMutation<TaskRow, TaskSpec>(
    (spec) => (isNew ? post<TaskRow>("/tasks", spec) : put<TaskRow>(`/tasks/${spec.id}`, spec)),
    {
      invalidates: [qk.tasks()],
      successMessage: S.taskSaved,
      errorTitle: S.mutationFailed,
      onSuccess: (saved) => navigate(`/tasks/${saved.id}`, { replace: true }),
    },
  );

  function submit() {
    const next: Record<string, string> = {};
    if (!draft.name.trim()) next.name = S.nameRequired;
    if (!draft.goal.trim()) next.goal = S.goalRequired;
    setErrors(next);
    if (Object.keys(next).length) return;
    save.mutate({
      ...draft,
      name: draft.name.trim(),
      goal: draft.goal.trim(),
      // Only the names the spec still uses: an edit that drops a placeholder must not leave
      // its value behind to reappear if the placeholder ever comes back.
      variables: Object.fromEntries(used.map((v) => [v, variables[v] ?? ""]).filter(([, x]) => x)),
    } as TaskSpec & { variables: Record<string, string> });
  }

  return (
    <>
      <BackLink />

      <PageHeader
        title={isNew ? S.newTask : S.editTask}
        hint={!isNew ? <span className="font-mono">{draft.id}</span> : undefined}
        actions={
          <Guarded permission="edit_task">
            <Button variant="primary" onClick={submit} disabled={save.isPending}>
              <Save size={13} />
              {save.isPending ? S.saving : S.save}
            </Button>
          </Guarded>
        }
      />

      <div className="max-w-3xl space-y-4">
        <Guarded permission="edit_task" mode="hide">
          <TaskAssistant
            current={draft}
            taskId={isNew ? null : draft.id}
            onApply={(d) => {
              // The id is the row's identity and stays ours; everything else is the proposal.
              setDraft((prev) => ({ ...d.spec, id: prev.id || d.spec.id }));
              setUnusedVariables(d.declared_variables ?? []);
            }}
          />
        </Guarded>

        {/* ---------------- 基本 ---------------- */}
        <Section title={S.sectionBasic}>
          <Field label={S.fieldName} error={errors.name}>
            <Input
              value={draft.name}
              onChange={(e) => set("name", e.target.value)}
              aria-invalid={!!errors.name}
            />
          </Field>

          <Field label={S.fieldGoal} hint={S.fieldGoalHint} error={errors.goal}>
            <Textarea
              value={draft.goal}
              onChange={(e) => set("goal", e.target.value)}
              rows={4}
              className="font-sans"
              aria-invalid={!!errors.goal}
            />
          </Field>

          <ToggleField
            label={S.fieldEnabledTask}
            hint={S.fieldEnabledTaskHint}
            checked={draft.enabled}
            onChange={(v) => set("enabled", v)}
          />
        </Section>

        {used.length > 0 && (
          <Section title={S.sectionVariables} hint={S.variablesHint}>
            {used.map((name) => (
              <Field key={name} label={`{{${name}}}`} hint={S.variableDefault}>
                <Input
                  value={variables[name] ?? ""}
                  onChange={(e) =>
                    setVariables((v) => ({ ...v, [name]: e.target.value }))
                  }
                />
              </Field>
            ))}
            {unusedVariables.length > 0 && (
              <p className="text-[12px] text-[var(--color-fg-subtle)]">
                {S.variablesUnused}: {unusedVariables.join(", ")}
              </p>
            )}
          </Section>
        )}

        {/* ---------------- 權限 ---------------- */}
        <Section title={S.sectionPermissions}>
          <Field label={S.fieldAllowedApps}>
            <AppPicker
              value={draft.allowedPackages}
              onChange={(v) => set("allowedPackages", v)}
              deviceId={null}
            />
          </Field>

          <ToggleField
            label={S.fieldAllowSend}
            hint={S.fieldAllowSendDesc}
            checked={draft.allowSend}
            onChange={(v) => set("allowSend", v)}
          />

          <Field label={S.fieldBlockedRegex} hint={S.fieldBlockedRegexHint}>
            <Input
              value={draft.blockedLabelsRegex ?? ""}
              onChange={(e) => set("blockedLabelsRegex", e.target.value || null)}
              className="font-mono text-[12px]"
              placeholder="(?i)(購買|轉帳|刪除|delete|purchase)"
            />
          </Field>

          <ToggleField
            label={S.fieldRequireUnlock}
            hint={S.fieldRequireUnlockDesc}
            checked={draft.requireUnlock}
            onChange={(v) => set("requireUnlock", v)}
          />
        </Section>

        {/* ---------------- Agent ---------------- */}
        <Section title={S.sectionAgent}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={S.fieldVision}>
              <Select
                value={draft.visionMode}
                onChange={(e) => set("visionMode", e.target.value as VisionMode)}
                options={VISION_MODES.map((v) => ({ value: v, label: visionModeLabel(v) }))}
              />
            </Field>

            <Field label={S.fieldThinking}>
              <Select
                value={draft.thinkingPolicy}
                onChange={(e) => set("thinkingPolicy", e.target.value as ThinkingPolicy)}
                options={THINKING_POLICIES.map((p) => ({
                  value: p,
                  label: thinkingPolicyLabel(p),
                }))}
              />
            </Field>

            <Field label={S.fieldMaxSteps}>
              <Input
                type="number"
                min={1}
                value={draft.maxSteps}
                onChange={(e) => set("maxSteps", Math.max(1, Number(e.target.value) || 1))}
                className="max-w-[140px]"
              />
            </Field>

            <Field label={S.fieldTimeout}>
              <Input
                type="number"
                min={1}
                value={draft.runTimeoutMinutes}
                onChange={(e) =>
                  set("runTimeoutMinutes", Math.max(1, Number(e.target.value) || 1))
                }
                className="max-w-[140px]"
              />
            </Field>
          </div>
        </Section>

        {/* ---------------- 擷取 ---------------- */}
        <Section title={S.sectionExtraction}>
          <Field label={S.fieldSchema}>
            <SchemaEditor
              value={draft.extractionSchema}
              onChange={(v) => set("extractionSchema", v)}
            />
          </Field>
        </Section>

        {/* ---------------- 排程 ---------------- */}
        <Section title={S.sectionSchedule}>
          <ScheduleBuilder
            value={draft.schedule}
            onChange={(s) => set("schedule", s)}
            deviceCount={deviceCount}
          />
        </Section>

        {/* ---------------- 交付 ---------------- */}
        <Section title={S.sectionDelivery}>
          <Field label={S.fieldWebhook} hint={S.fieldWebhookHint}>
            <Input
              type="url"
              value={draft.webhookUrl ?? ""}
              onChange={(e) => set("webhookUrl", e.target.value || null)}
              placeholder="https://example.com/hooks/infocollect"
              className="font-mono text-[12px]"
            />
          </Field>
        </Section>

        <div className="flex justify-end gap-2 pb-4">
          <Button variant="ghost" onClick={() => navigate("/tasks")}>
            {S.cancel}
          </Button>
          <Guarded permission="edit_task">
            <Button variant="primary" onClick={submit} disabled={save.isPending}>
              <Save size={13} />
              {save.isPending ? S.saving : S.save}
            </Button>
          </Guarded>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Layout helpers
 * ------------------------------------------------------------------ */

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="pt-4">
        <h2 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-fg-subtle)]">
          {title}
        </h2>
        {hint && <p className="mb-3 text-[12px] text-[var(--color-fg-subtle)]">{hint}</p>}
        <div className={hint ? "space-y-3" : "mt-3 space-y-3"}>{children}</div>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 text-[12px] font-medium text-[var(--color-fg)]">{label}</div>
      {children}
      {error ? (
        <p className="mt-1 text-[11px] text-[#fca5a5]">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[11px] text-[var(--color-fg-faint)]">{hint}</p>
      ) : null}
    </div>
  );
}

function ToggleField({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Switch checked={checked} onCheckedChange={onChange} className="mt-0.5" />
      <div>
        <div className="text-[12px] font-medium text-[var(--color-fg)]">{label}</div>
        {hint && <p className="text-[11px] text-[var(--color-fg-faint)]">{hint}</p>}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/tasks"
      className="mb-3 inline-flex items-center gap-1 text-[12px] text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]"
    >
      <ArrowLeft size={12} />
      {S.tasksTitle}
    </Link>
  );
}
